/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { CardMetadata, ProductCard, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { EnterpriseBadgeWithCollection } from 'bundles/page-config-common/providers/enterprise/EnterpriseBadgeCollectionsProvider';
import { getStatusTagRedesignExperimentVariant } from 'bundles/product-card/components/utils/getStatusTagRedesignExperimentVariant';

import _t from 'i18n!nls/product-card';

type BaseProps = {
  enterpriseBadgeWithCollection: EnterpriseBadgeWithCollection;
  isDesktopListView?: boolean;
  itemIndex: number;
  isSkillPathway?: boolean;
};

type Props = BaseProps & ({ isOrgScoped: true; orgSlug: string } | { isOrgScoped?: false; orgSlug?: never });

const styles = {
  targetCardWrapper: css`
    height: 100%;
  `,

  targetCardWrapperCtrl: css`
    .cds-ProductCard-list {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
  learningPathProductCardImageWrapper: css`
    height: 100%;
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  learningPathProductCardImage: css`
    object-fit: contain !important;

    ${breakpoints.up('md')} {
      padding: var(--cds-spacing-50);
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,
  svg: css`
    margin-bottom: -2px;
  `,
  tagRight: css`
    .cds-ProductCard-statusTagsOverlay {
      right: 0;
      left: unset;
    }
  `,
};

export const LearningPathProductCardImage = ({ src }: { src: string }) => {
  return (
    <div css={styles.learningPathProductCardImageWrapper}>
      <img src={src} alt="" css={styles.learningPathProductCardImage} />
    </div>
  );
};

const EnterpriseLearningPathProductCard = ({
  enterpriseBadgeWithCollection,
  isDesktopListView,
  itemIndex,
  isSkillPathway,
  isOrgScoped,
  ...rest
}: Props) => {
  const orgSlug = isOrgScoped ? rest.orgSlug : undefined;
  const track = useTracker();
  const { programSlug } = useParams();
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));

  const shouldShowListView = isDesktopListView || isTabletOrSmaller;

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    {
      productCard: {
        index: itemIndex,
      },
      product: {
        id: enterpriseBadgeWithCollection?.enterpriseBadge?.badgeTemplateId,
        slug: enterpriseBadgeWithCollection?.enterpriseBadge?.slug,
        type: 'learning_path',
        ...(isSkillPathway ? { verifiedSkillPath: true } : {}),
      },
      enterprise: {
        ...(!orgSlug && programSlug ? { programSlug } : {}),
      },
    }
  );

  const badgeSlug = enterpriseBadgeWithCollection.enterpriseBadge.slug;
  const titleHref = orgSlug
    ? `/organizations/${orgSlug}/learning-path/${badgeSlug}`
    : `/programs/${programSlug}/learning-path/${badgeSlug}?collectionId=${enterpriseBadgeWithCollection.enterpriseBadge.collectionId}`;

  const { isBorderlessCard, productCardExperimentVariant } = getStatusTagRedesignExperimentVariant();
  const variantType = !shouldShowListView ? 'grid' : 'list';

  return (
    <div
      css={[
        styles.targetCardWrapper,
        !isBorderlessCard && styles.targetCardWrapperCtrl,
        variantType === 'list' ? styles.listCardContainer : null,
      ]}
    >
      <ProductCard
        experimentVariant={productCardExperimentVariant}
        ref={visibilityTrackingRef}
        data-testid="learning-path"
        productType={_t('Learning Path')}
        variant={variantType}
        css={styles.tagRight}
        title={{
          name: enterpriseBadgeWithCollection.enterpriseBadge.title,
          linkProps: {
            href: titleHref,
          },
        }}
        renderPreviewImage={() => (
          <LearningPathProductCardImage src={enterpriseBadgeWithCollection.enterpriseBadge.imageUrl} />
        )}
        footer={
          <>
            <CardMetadata metadata={[_t('Learning Path')]} />
          </>
        }
        onClick={() => {
          track('click_product_card', {
            productCard: {
              index: itemIndex,
            },
            product: {
              id: enterpriseBadgeWithCollection?.enterpriseBadge?.badgeTemplateId,
              slug: enterpriseBadgeWithCollection?.enterpriseBadge?.slug,
              type: 'learning_path',
              ...(isSkillPathway ? { verifiedSkillPath: true } : {}),
            },
            enterprise: {
              ...(!orgSlug && programSlug ? { programSlug } : {}),
            },
            enterpriseCurationLibraries: {
              location: 'learning_path_curation',
            },
          });
        }}
      />
    </div>
  );
};

export default EnterpriseLearningPathProductCard;
